// One plane of per-cell data, on WebGPU (the runtime design §4).
//
// It is the twin of the `DataTexture` of `gl/texture.ts`, and the CPU half is
// the shared `CellPlane` of `gfx/plane.ts`: one sovereignty flip writes one byte and marks the plane dirty,
// and the frame loop then uploads the whole 20 KB plane once.
//
// Two things differ on WebGPU:
//
//  1. **The half floats are packed here.** WebGL2 takes a `Float32Array` into
//     an `R16F` texture and the driver converts. `queue.writeTexture` copies
//     BYTES, so a float plane packs through `gfx/f16.ts` into a kept
//     `Uint16Array` first.
//  2. **`bytesPerRow` is the row as it stands**, `width x channels x bytes`,
//     with no padding at all. The 256-byte row rule belongs to
//     `copyBufferToTexture` and not to `writeTexture`, so the 133-wide
//     one-byte rows of the core grid upload as they are. It is the WebGPU
//     form of the `UNPACK_ALIGNMENT 1` that `gl/context.ts` sets.

import { packF16 } from '../gfx/f16';
import { CellPlane, channelsOf } from '../gfx/plane';
import { TEXTURE_BINDING, TEXTURE_COPY_DST } from './flags';
import { bindUnit } from './bindgroups';
import type { DataFormat, DataTexture as DataTextureApi } from '../gfx/types';
import type { Gpu, GpuResource } from './context';

/** The WebGPU format of each of the five per-cell formats. */
export function gpuFormatOf(format: DataFormat): GPUTextureFormat {
  switch (format) {
    case 'r8ui': return 'r8uint';
    case 'r8': return 'r8unorm';
    case 'r16f': return 'r16float';
    case 'rg16f': return 'rg16float';
    case 'rgba16f': return 'rgba16float';
  }
}

/** How many bytes one texel of a format takes on the wire to the GPU. */
export function bytesPerTexel(format: DataFormat): number {
  return (format === 'r8ui' || format === 'r8' ? 1 : 2) * channelsOf(format);
}

export class GpuDataTexture extends CellPlane implements GpuResource, DataTextureApi {
  texture: GPUTexture | null = null;
  /** The id the group-1 cache is keyed by. A new texture takes a new one. */
  id = 0;

  private view: GPUTextureView | null = null;
  private halves: Uint16Array | null = null;

  constructor(
    private readonly gpu: Gpu,
    name: string,
    width: number,
    height: number,
    format: DataFormat,
    /** LINEAR is only legal on a float format. The default is NEAREST. */
    linear = false,
  ) {
    super(name, width, height, format, linear);
    gpu.register(this);
  }

  create(device: GPUDevice): void {
    this.destroy();
    this.texture = device.createTexture({
      label: `fast: ${this.name}`,
      size: [Math.max(1, this.width), Math.max(1, this.height)],
      format: gpuFormatOf(this.format),
      usage: TEXTURE_BINDING | TEXTURE_COPY_DST,
    });
    this.view = this.texture.createView();
    this.id = this.gpu.textureId();
    this.markDirty();
  }

  destroy(): void {
    if (this.texture) this.texture.destroy();
    this.texture = null;
    this.view = null;
    this.id = 0;
  }

  /**
   * One `writeTexture` of the whole plane, and only when it is dirty. A
   * plane that `markRows` marked copies those rows alone (R2897).
   */
  upload(): boolean {
    if (!this.dirty || !this.texture || this.gpu.lost) return false;
    // The row as it stands. `writeTexture` takes any `bytesPerRow`; the
    // 256-byte rule is `copyBufferToTexture`'s alone.
    const bytesPerRow = this.width * bytesPerTexel(this.format);
    const bytes = this.bytesToUpload();
    if (this.rowLo >= 0) {
      const r0 = this.upRow, rows = this.upRows;
      this.gpu.queue.writeTexture(
        { texture: this.texture, origin: { x: 0, y: r0 } },
        bytes,
        { offset: r0 * bytesPerRow, bytesPerRow, rowsPerImage: rows },
        { width: this.width, height: rows },
      );
    } else {
      this.gpu.queue.writeTexture(
        { texture: this.texture },
        bytes,
        { bytesPerRow, rowsPerImage: this.height },
        { width: this.width, height: this.height },
      );
    }
    this.uploaded();
    return true;
  }

  bind(unit: number): void {
    if (!this.view || this.gpu.lost) return;
    bindUnit(this.gpu.units, unit, this.view, this.id);
  }

  dispose(): void { this.gpu.unregister(this); }

  /**
   * The bytes the upload copies.
   *
   * A one-byte plane goes as it is. A float plane packs through the kept
   * `Uint16Array`, which is allocated once and reused, so a 3.5 M float plane
   * uploads with no allocation at all.
   */
  private bytesToUpload(): ArrayBufferView {
    if (this.data instanceof Uint8Array) return this.data;
    const n = this.data.length;
    if (!this.halves || this.halves.length !== n) {
      // A new pack buffer holds no row yet: the first upload packs all.
      this.halves = new Uint16Array(n);
      this.markDirty();
    }
    // The rows of a `markRows` alone pack again (R2897); every other row
    // of the kept buffer already holds the plane.
    const per = this.width * this.channels;
    const from = this.upRow * per;
    packF16(this.data, this.halves, from + this.upRows * per, from);
    return this.halves;
  }
}
